import { useNavigate } from 'react-router-dom';
import {
  buildVenuesListingPath,
  buildVendorsListingPath,
} from '../../../utils/listingPaths';
import { cities, citiesSection } from '../../../config/appData';
import './CitiesSection.css';

// Load all city images from assets/cities - filenames match city names (with known file naming variations)
const cityImagesContext = require.context('../../../assets/cities', false, /\.(jpg|jpeg|png|webp|avif)$/i);

// Map config city name -> exact filename in assets/cities (handles typos/variations in file names)
const cityNameToFilename = {
  Agra: 'Agra.jpg',
  Ahmedabad: 'ahemdabad.webp',
  Ajmer: 'ajmer.jpg',
  Aligarh: 'aligarh.jpeg',
  Ambala: 'ambala.jpeg',
  Amritsar: 'Amritsar.jpg',
  Amroha: 'Amroha.jpeg',
  Asansol: 'asansol.jpg',
  Aurangabad: 'auraungabad.avif',
  Bangalore: 'banglore.jpg',
  Bareilly: 'Braeily.jpeg',
  Bhagalpur: 'bhagalpur.avif',
  Bhopal: 'bhopal.jpeg',
  Bhubaneswar: 'bhubaneswar.jpg',
  Bikaner: 'bikaner.jpeg',
  Bilaspur: 'bilaspur.avif',
  Chandigarh: 'chandigarh.jpeg',
  Chennai: 'chennai.jpg',
  Coimbatore: 'coimbatore.jpeg',
  Cuttack: 'cuttack.jpg',
  Dehradun: 'dehradun.jpg',
  'Delhi NCR': 'delhincr.jpeg',
  Dhanbad: 'dhanbad.jpg',
  Dibrugarh: 'dibrugarh.jpg',
  Durg: 'durg.jpeg',
  Durgapur: 'durgapur.jpeg',
  Faridabad: 'faridabad.jpeg',
  Gajraula: 'gajraula.jpeg',
  Gandhinagar: 'gandhinagar.webp',
  Garhmukteshwar: 'garhmukteshwar.webp',
  Ghaziabad: 'ghaziabad.jpg',
  Goa: 'Goa.jpg',
  Gurugram: 'gurugram.jpeg',
  Guwahati: 'guwahati.jpg',
  Gwalior: 'gwalior.avif',
  Haldwani: 'haldwani.jpg',
  Hapur: 'hapur.jpeg',
  Haridwar: 'haridwar.webp',
  Hubli: 'hubli.avif',
  Hyderabad: 'hyderabad.jpg',
  Indore: 'indore.jpeg',
  Jabalpur: 'jabalpur.jpeg',
  Jaipur: 'jaipur.jpeg',
  Jalandhar: 'jalandhar.jpeg',
  Jodhpur: 'jodhpur.jpg',
  Kanpur: 'kanpur.jpeg',
  Karimnagar: 'karimnagar.jpeg',
  Kasol: 'kasol.jpg',
  Kochi: 'kochi.jpeg',
  Kolkata: 'kolkata.jpeg',
  Kolhapur: 'kolhapur.jpeg',
  Kota: 'kota.jpg',
  Kozhikode: 'kozhikode.jpg',
  Lucknow: 'lucknow.jpeg',
  Ludhiana: 'ludhiana.jpeg',
  Madurai: 'madurai.jpeg',
  Maldives: 'Maldives.jpg',
  Manali: 'manali.webp',
  Mangalore: 'mangalore.jpeg',
  Meerut: 'merrut.jpg',
  Moradabad: 'Moradabad.jpg',
  Moradnagar: 'Moradnagar.jpg',
  Mumbai: 'mumbai.jpeg',
  Muzaffarnagar: 'Muzzafarnagar.jpg',
  Muzaffarpur: 'muzaffarpur.jpg',
  Mysore: 'maysore.jpg',
  Nagpur: 'nagpur.jpeg',
  Nainital: 'Nainital.avif',
  Nashik: 'nashik.jpeg',
  'New Delhi': 'new delhi.webp',
  Noida: 'noida.avif',
  Panipat: 'panipat.jpg',
  Patiala: 'patiala.jpeg',
  Patna: 'patna.jpg',
  Prayagraj: 'pyagraj.jpeg',
  Pune: 'pune.jpeg',
  Raipur: 'raipur.jpeg',
  Rajkot: 'rajkot.jpg',
  Ramnagar: 'ramnagar.jpeg',
  Rampur: 'Rampur.jpg',
  Ranchi: 'ranchi.jpeg',
  Rishikesh: 'Rishikesh.jpg',
  Rohtak: 'rohtak.jpeg',
  Rourkela: 'rourkela.jpg',
  Rudrapur: 'rudrapur.jpeg',
  Saharanpur: 'sahranpur.jpg',
  Salem: 'salem.jpeg',
  Shimla: 'shimla.avif',
  Siliguri: 'siliguri.jpeg',
  Sonipat: 'sonipat.jpeg',
  Srinagar: 'srinagar.jpg',
  Surat: 'surat.webp',
  Thane: 'thane.jpeg',
  Thiruvananthapuram: 'thiruvananthapuram.jpg',
  Tiruchirappalli: 'tiruchirappalli.jpeg',
  Udaipur: 'Udaipur.jpeg',
  Ujjain: 'ujjain.jpeg',
  Vadodara: 'vadodara.jpeg',
  Varanasi: 'varanasi.jpeg',
  Warangal: 'warangal.avif',
};

// Fallback image key (Noida) when city has no image or filename not found
const FALLBACK_KEY = './noida.avif';

function getContextImageUrl(key) {
  if (!cityImagesContext.keys().includes(key)) return null;
  const m = cityImagesContext(key);
  return m && typeof m === 'object' && 'default' in m ? m.default : m;
}

function getCityImageUrl(city) {
  const filename = cityNameToFilename[city];
  if (!filename) return getContextImageUrl(FALLBACK_KEY);
  const key = './' + filename;
  return getContextImageUrl(key) || getContextImageUrl(FALLBACK_KEY);
}

// Legacy export: object of city -> image URL for any other consumers
const cityImageMap = {};
cityImagesContext.keys().forEach((key) => {
  const filename = key.replace('./', '');
  const city = Object.entries(cityNameToFilename).find(([, f]) => f === filename)?.[0];
  if (city) cityImageMap[city] = getContextImageUrl(key);
});
if (cityImagesContext.keys().includes(FALLBACK_KEY)) {
  const fallbackUrl = getContextImageUrl(FALLBACK_KEY);
  cities.forEach((c) => {
    if (!cityImageMap[c]) cityImageMap[c] = fallbackUrl;
  });
}

function CitiesSection({
  cityList,
  title = citiesSection.title,
  subtitle = citiesSection.subtitle,
  sectionId = 'cities-section',
}) {
  const navigate = useNavigate();
  const displayCities =
    Array.isArray(cityList) && cityList.length > 0 ? cityList : cities;

  const handleBanquetHallsClick = (city, e) => {
    e.stopPropagation();
    navigate(buildVenuesListingPath("all", { city }));
  };

  const handleVendorsClick = (city, e) => {
    e.stopPropagation();
    navigate(buildVendorsListingPath({ city }));
  };

  return (
    <section id={sectionId} className="cities-section">
      <div className="cities-container">
        <h2 className="cities-title">{title}</h2>
        <p className="cities-subtitle">{subtitle}</p>
        <div className="cities-grid">
          {displayCities.map((city, index) => (
            <div key={index} className="city-card">
              <div className="city-card-image-wrapper">
                <img
                  src={getCityImageUrl(city)}
                  alt={`${city} wedding venues`}
                  className="city-card-image"
                  loading="lazy"
                />
                <div className="city-card-overlay"></div>
                <div className="city-card-name-overlay">{city}</div>
              </div>
              <div className="city-card-content">
                <div className="city-card-ctas">
                  <button
                    className="city-cta-btn banquet-halls-cta"
                    onClick={(e) => handleBanquetHallsClick(city, e)}
                    type="button"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                    <span>  Banquet Halls</span>
                    <span>→</span>
                  </button>
                  <button
                    className="city-cta-btn vendors-cta"
                    onClick={(e) => handleVendorsClick(city, e)}
                    type="button"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                    <span>{citiesSection.vendorsCta}</span>
                    <span>→</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default CitiesSection;
export { cityImageMap, cityNameToFilename };
export { getCityImageUrl };
